import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { type SnackbarFactory } from '@/features/shell'
import { type GetCurrentMemberFn } from '@/features/iam'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { observable, runInAction } from 'mobx'
import { task } from 'mobx-task'

const SNACKBAR_KEY = Symbol('Copy code')

/**
 * View model for the refer-a-friend banner.
 */
export class ReferAFriendViewModel {
  /**
   * Modal to display the code.
   */
  readonly modal = new ModalViewModel()

  /**
   * The referral code.
   */
  @observable accessor code = ''

  constructor(
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _copyToClipboard: (text: string) => Promise<void>,
  ) {
    this.open = this.open.bind(this)
    this.copyCodeToClipboard = this.copyCodeToClipboard.bind(this)
  }

  /**
   * Open the modal and fetch the referral code.
   */
  open() {
    void this.fetchCode()
    this.modal.show()
  }

  /**
   * Fetches the coupon code.
   */
  fetchCode = task(async () => {
    const member = this._getCurrentMember()
    const code = await this._billingProfileStore
      .fetchBillingProfileForMember(member)
      .then((r) => r.referralCouponCode)
    runInAction(() => (this.code = code))
  })

  /**
   * Copies the code to the clipboard.
   */
  async copyCodeToClipboard() {
    const snackbar = this._newSnackbar((s) => s.withKey(SNACKBAR_KEY))
    const code = this.code
    if (!code) {
      return
    }

    try {
      await this._copyToClipboard(code)
      snackbar.update((s) =>
        s
          .type('success')
          .saying('Copied!', 'The code was copied to the clipboard.')
          .show()
          .autoDismiss(),
      )
    } catch (err: unknown) {
      snackbar.fromError(err)
    }
  }
}
